import React, { useState } from "react";

interface Activity {
  id: string;
  date: string;
  time: string;
  title: string;
  type: string;
  description: string;
  facilitator: string;
  community: boolean;
  communityPartner: string;
  participants: string[];
  maxCapacity: number;
  status: string;
  feedback: string;
  rating: number;
}

const KEY = "activities_records";
const TYPES = ["Social", "Creative", "Physical", "Cognitive", "Spiritual", "Sensory", "Music", "Outdoor", "Community outing", "Visiting entertainer", "Intergenerational", "1-to-1"];

function load(): Activity[] {
  try {
    return JSON.parse(localStorage.getItem(KEY) || "[]");
  } catch {
    return [];
  }
}

function loadResidents(): any[] {
  try {
    return JSON.parse(localStorage.getItem("residents_records") || "[]");
  } catch {
    return [];
  }
}

const today = () => new Date().toISOString().slice(0, 10);

export default function Activities({ user }: { user?: any }) {
  const [items, setItems] = useState<Activity[]>(load);
  const residents = loadResidents();
  const [form, setForm] = useState<Partial<Activity>>({ date: today(), time: "14:00", type: "Social", status: "Planned", community: false, participants: [], maxCapacity: 20, rating: 0 });
  const [message, setMessage] = useState("");
  const [viewMonth, setViewMonth] = useState(today().slice(0, 7));
  const [editId, setEditId] = useState("");

  const save = (next: Activity[]) => {
    setItems(next);
    localStorage.setItem(KEY, JSON.stringify(next));
  };

  const set = (key: string, value: any) => setForm(f => ({ ...f, [key]: value }));

  const toggleParticipant = (name: string) => {
    const list = form.participants || [];
    set("participants", list.includes(name) ? list.filter(n => n !== name) : [...list, name]);
  };

  const saveActivity = () => {
    if (!form.title) {
      setMessage("Enter the activity title.");
      return;
    }
    const rec: Activity = {
      id: editId || `act-${Date.now()}`,
      date: form.date || today(),
      time: form.time || "14:00",
      title: form.title || "",
      type: form.type || "Social",
      description: form.description || "",
      facilitator: form.facilitator || (user && user.name ? user.name : ""),
      community: !!form.community,
      communityPartner: form.communityPartner || "",
      participants: form.participants || [],
      maxCapacity: parseInt(String(form.maxCapacity), 10) || 20,
      status: form.status || "Planned",
      feedback: form.feedback || "",
      rating: parseInt(String(form.rating), 10) || 0,
    };
    const exists = items.some(i => i.id === rec.id);
    save(exists ? items.map(i => (i.id === rec.id ? rec : i)) : [rec, ...items]);
    setForm({ date: today(), time: "14:00", type: "Social", status: "Planned", community: false, participants: [], maxCapacity: 20, rating: 0 });
    setEditId("");
    setMessage(`Activity ${exists ? "updated" : "saved"}: ${rec.title}`);
  };

  const edit = (a: Activity) => { setForm(a); setEditId(a.id); };
  const remove = (id: string) => save(items.filter(i => i.id !== id));

  const monthItems = items.filter(i => (i.date || "").slice(0, 7) === viewMonth);
  const completed = monthItems.filter(i => i.status === "Completed");
  const communityCount = monthItems.filter(i => i.community).length;
  const totalParticipations = completed.reduce((s, i) => s + (i.participants || []).length, 0);

  const residentEngagement = residents.map((r: any) => {
    const count = completed.filter(a => (a.participants || []).includes(r.name)).length;
    return { name: r.name, count };
  }).sort((a, b) => a.count - b.count);

  const lastMonths = (n: number) => {
    const out: string[] = [];
    const d = new Date();
    for (let i = n - 1; i >= 0; i--) {
      const dt = new Date(d.getFullYear(), d.getMonth() - i, 1);
      out.push(`${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`);
    }
    return out;
  };

  const trendMonths = lastMonths(6);
  const trendData = trendMonths.map(m => items.filter(i => (i.date || "").slice(0, 7) === m && i.status === "Completed").length);
  const maxTrend = Math.max(1, ...trendData);

  const printCalendar = () => {
    const rows = monthItems.sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time)).map(a => `<tr><td style="border-bottom:1px solid #eee;padding:6px">${a.date}</td><td style="border-bottom:1px solid #eee;padding:6px">${a.time}</td><td style="border-bottom:1px solid #eee;padding:6px">${a.title}</td><td style="border-bottom:1px solid #eee;padding:6px">${a.type}${a.community ? " (Community)" : ""}</td><td style="border-bottom:1px solid #eee;padding:6px">${a.status}</td><td style="border-bottom:1px solid #eee;padding:6px">${(a.participants || []).length} participants</td></tr>`).join("");
    const html = `<!DOCTYPE html><html><head><title>Activities Calendar</title><style>body{font-family:Arial;margin:40px;color:#1f2937}h2{color:#123B36}table{width:100%;border-collapse:collapse;font-size:13px}</style></head><body><h2>Activities & Community Calendar - Roop Cottage</h2><p>${viewMonth} | ${monthItems.length} activities | ${communityCount} community | ${totalParticipations} participations</p><table><tr><th>Date</th><th>Time</th><th>Activity</th><th>Type</th><th>Status</th><th>Participants</th></tr>${rows}</table></body></html>`;
    const win = window.open("", "_blank");
    if (win) { win.document.write(html); win.document.close(); win.focus(); win.print(); }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-xl font-semibold text-slate-900">Activities & Community Integration</h2>
          <p className="text-sm text-slate-600">Planned and completed activities, resident engagement tracking, community partnerships and participation trends - Caring and Responsive at Outstanding level.</p>
        </div>
        <button onClick={printCalendar} className="rounded-lg bg-red-700 px-4 py-2 text-sm font-medium text-white">Print calendar</button>
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm"><div className="text-2xl font-bold text-slate-900">{monthItems.length}</div><div className="text-xs text-slate-500">Activities this month</div></div>
        <div className="rounded-xl border border-green-300 bg-green-50 p-4 text-center"><div className="text-2xl font-bold text-green-700">{completed.length}</div><div className="text-xs text-green-700">Completed</div></div>
        <div className="rounded-xl border border-blue-300 bg-blue-50 p-4 text-center"><div className="text-2xl font-bold text-blue-700">{communityCount}</div><div className="text-xs text-blue-700">Community events</div></div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 text-center shadow-sm"><div className="text-2xl font-bold text-slate-900">{totalParticipations}</div><div className="text-xs text-slate-500">Total participations</div></div>
      </div>

      {message ? <div className="rounded-lg bg-blue-50 p-3 text-sm text-blue-900">{message}</div> : null}

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">{editId ? "Edit activity" : "Plan an activity"}</h3>
        <div className="grid gap-3 md:grid-cols-4">
          <input type="date" value={form.date || ""} onChange={e => set("date", e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <input type="time" value={form.time || ""} onChange={e => set("time", e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <input value={form.title || ""} onChange={e => set("title", e.target.value)} placeholder="Activity title" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <select value={form.type || "Social"} onChange={e => set("type", e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
            {TYPES.map(t => <option key={t}>{t}</option>)}
          </select>
          <textarea value={form.description || ""} onChange={e => set("description", e.target.value)} rows={2} placeholder="Description" className="rounded-lg border border-slate-300 px-2 py-1 text-sm md:col-span-2" />
          <input value={form.facilitator || ""} onChange={e => set("facilitator", e.target.value)} placeholder="Facilitator" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
          <select value={form.status || "Planned"} onChange={e => set("status", e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1 text-sm">
            <option>Planned</option><option>Completed</option><option>Cancelled</option>
          </select>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!form.community} onChange={e => set("community", e.target.checked)} />Community event</label>
          {form.community ? <input value={form.communityPartner || ""} onChange={e => set("communityPartner", e.target.value)} placeholder="Community partner" className="rounded-lg border border-slate-300 px-2 py-1 text-sm" /> : null}
          <div className="md:col-span-4">
            <label className="mb-1 block text-xs font-medium text-slate-600">Participants (tick who attended)</label>
            <div className="flex flex-wrap gap-2">
              {residents.map((r: any) => (
                <label key={r.id} className="flex items-center gap-1 text-xs"><input type="checkbox" checked={(form.participants || []).includes(r.name)} onChange={() => toggleParticipant(r.name)} />{r.name}</label>
              ))}
            </div>
          </div>
          {form.status === "Completed" ? (
            <>
              <textarea value={form.feedback || ""} onChange={e => set("feedback", e.target.value)} rows={2} placeholder="Resident feedback / observations" className="rounded-lg border border-slate-300 px-2 py-1 text-sm md:col-span-3" />
              <div>
                <label className="mb-1 block text-xs font-medium text-slate-600">Enjoyment rating (1-5)</label>
                <select value={String(form.rating || 0)} onChange={e => set("rating", e.target.value)} className="w-full rounded-lg border border-slate-300 px-2 py-1 text-sm">
                  <option value="0">-</option><option value="1">1</option><option value="2">2</option><option value="3">3</option><option value="4">4</option><option value="5">5</option>
                </select>
              </div>
            </>
          ) : null}
        </div>
        <div className="mt-3 flex gap-2">
          <button onClick={saveActivity} className="rounded-lg bg-green-700 px-4 py-2 text-sm font-medium text-white">{editId ? "Update" : "Save activity"}</button>
          {editId ? <button onClick={() => { setEditId(""); setForm({ date: today(), time: "14:00", type: "Social", status: "Planned", community: false, participants: [], maxCapacity: 20, rating: 0 }); }} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">Cancel</button> : null}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">Resident engagement (lowest first)</h3>
        <div className="space-y-1">
          {residentEngagement.map(r => (
            <div key={r.name} className="flex items-center justify-between rounded-lg border border-slate-100 p-2 text-sm">
              <div>{r.name}</div>
              <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${r.count === 0 ? "bg-red-100 text-red-700" : r.count <= 2 ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{r.count} activities</span>
            </div>
          ))}
          {residentEngagement.length === 0 ? <div className="text-sm text-slate-500">No residents yet.</div> : null}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <h3 className="mb-3 font-semibold text-slate-900">Six-month activity trend</h3>
        <div className="flex h-32 items-end gap-2">
          {trendMonths.map((m, i) => (
            <div key={m} className="flex flex-1 flex-col items-center gap-1">
              <div className="text-xs text-slate-600">{trendData[i] || ""}</div>
              <div className="w-full rounded-t" style={{ height: `${(trendData[i] / maxTrend) * 100}%`, minHeight: trendData[i] ? "6px" : "2px", background: "#16a34a" }} />
              <div className="text-xs text-slate-500">{new Date(m + "-01").toLocaleDateString(undefined, { month: "short" })}</div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="mb-3 flex items-center gap-2">
          <h3 className="font-semibold text-slate-900">Calendar</h3>
          <input type="month" value={viewMonth} onChange={e => setViewMonth(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1 text-sm" />
        </div>
        <div className="space-y-2">
          {monthItems.sort((a, b) => a.date.localeCompare(b.date) || a.time.localeCompare(b.time)).map(a => (
            <div key={a.id} className="rounded-lg border border-slate-100 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium text-slate-900">{a.date} {a.time} - {a.title} <span className="text-xs text-slate-500">{a.type}{a.community ? ` (${a.communityPartner || "Community"})` : ""} | {(a.participants || []).length} participants</span></div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${a.status === "Completed" ? "bg-green-100 text-green-700" : a.status === "Cancelled" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{a.status}</span>
                  <button onClick={() => edit(a)} className="rounded-lg border border-slate-300 px-2 py-1 text-xs">Edit</button>
                  <button onClick={() => remove(a.id)} className="text-xs text-red-600">Remove</button>
                </div>
              </div>
              {a.feedback ? <div className="mt-1 text-xs text-slate-600">Feedback: {a.feedback}{a.rating ? ` | Rating: ${a.rating}/5` : ""}</div> : null}
            </div>
          ))}
          {monthItems.length === 0 ? <div className="text-sm text-slate-500">No activities this month.</div> : null}
        </div>
      </div>
    </div>
  );
}